/*eslint-disable*/
import moment from 'moment';
import { useCallback, useState } from 'react';

// @mui/material components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';

// material ui icons
import SearchResultsIcon from '@mui/icons-material/Reorder';
import SearchIcon from '@mui/icons-material/Search';
import ViewIcon from '@mui/icons-material/Visibility';

// core components
import DatePicker from 'components/CustomInput/CalendarInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import Table from 'components/Table/Table.js';

import CountHistory from './CountHistory';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

import { Typography } from '@mui/material';
import {
  useLazyGetCountHistoryQuery,
  useLazySearchCashCountsQuery,
} from 'api/cash-management.api';
import {
  useGetLocationsQuery,
  useLazyGetCashdrawersQuery,
} from 'api/location.api';
import { getCurrencyFormat } from '../../helpers/currency';
import { useGetTenantQuery } from 'api/tenant.api';
import { getStackTrace, log } from '@src/Log.init';
import { toast } from 'react-toastify';

const useStyles = makeStyles(styles);

export default function SearchCriteria(props) {
  const { data: tenant } = useGetTenantQuery();
  const { data: locationsData } = useGetLocationsQuery({ limit: 20 });
  const locations = locationsData?.results || [];
  const [triggerGetCashdrawers, { data: cashdrawers, isSuccess }] =
    useLazyGetCashdrawersQuery();
  const [
    triggerGetCashCounts,
    { data: counts, isSuccess: isCashCountsSuccess },
  ] = useLazySearchCashCountsQuery();
  const resultCount = isCashCountsSuccess && counts.metadata.total;
  const [
    triggerGetCountHistory,
    {
      data: countHistory,
      isSuccess: isHistorySuccess,
      isFetching: isHistoryFetching,
    },
  ] = useLazyGetCountHistoryQuery();
  let historyStatus;
  if (isHistoryFetching) {
    historyStatus = 'pending';
  } else if (isHistorySuccess) {
    historyStatus = 'success';
  } else {
    historyStatus = 'failure';
  }

  const classes = useStyles();

  const [dirtyData, setDirtyData] = useState(false);
  const [viewHistory, setViewHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [location, setLocation] = useState('');
  const [locationState, setLocationState] = useState('');
  const [cashdrawer, setCashdrawer] = useState('');
  const [cashdrawerState, setCashdrawerState] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startDateState, setStartDateState] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endDateState, setEndDateState] = useState('');

  const isEndDateValid = function (current) {
    const start = moment(startDate);
    if (startDate) return current.isAfter(start);
    else return true;
  };
  const isStartDateValid = function (current) {
    const end = moment(endDate);
    if (endDate) return current.isBefore(end);
    else return true;
  };

  const onClickSearch = async () => {
    await searchCashCounts();
  };

  const handleViewHistory = (index) => {
    triggerGetCountHistory(counts.data[index]._id)
      .unwrap()
      .catch((error) => {
        log.error({
          message_type: 'SystemError',
          message: 'getCountHistory: query failure',
          meta_data: getStackTrace(),
          error,
        });
      });
    setViewHistory(true);
  };

  const searchCashCounts = async (pg, rows) => {
    const searchCriteria = {};
    if (location && location !== '') searchCriteria.location = location;
    if (startDate && startDate !== '') searchCriteria.beginDate = startDate;
    if (endDate && endDate !== '') searchCriteria.endDate = endDate;
    if (cashdrawer && cashdrawer !== '') searchCriteria.cashdrawer = cashdrawer;
    searchCriteria.rowsPerPage = rows ? rows : rowsPerPage;
    searchCriteria.page = pg ? pg : page;

    try {
      await triggerGetCashCounts(searchCriteria).unwrap();
      setDirtyData(false);
    } catch (error) {
      toast.error('Error searching cash counts', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage + 1);
    setDirtyData(true);
    await searchCashCounts(newPage + 1);
  };

  const handleChangeRowsPerPage = async (event) => {
    const rowsPP = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPP);
    setPage(1);
    setDirtyData(true);
    await searchCashCounts(1, rowsPP);
  };

  const handleCloseHistory = () => {
    setViewHistory(false);
  };

  const locationList = () => {
    const locs = locations
      .filter((loc) => loc.status === 'active')
      .map((location, key) => {
        const option = (
          <MenuItem key={key} value={location.key}>
            {location.key + ', ' + location.name}
          </MenuItem>
        );
        return option;
      });
    return locs;
  };

  const cashdrawerList = () => {
    if (!isSuccess || !cashdrawers) return null;
    const locs = cashdrawers
      .filter((cashdrw) => cashdrw.status === 'Active')
      .map((cashdrawer, key) => {
        const option = (
          <MenuItem key={key} value={cashdrawer.cashdrawer_id}>
            {cashdrawer.cashdrawer_id + ', ' + cashdrawer.description}
          </MenuItem>
        );
        return option;
      });
    return locs;
  };

  const locationChangeHandler = (event) => {
    if (event.target.value !== location) {
      if (event.target.value !== '')
        triggerGetCashdrawers(event.target.value)
          .unwrap()
          .catch((error) =>
            log.error({
              message_type: 'SystemError',
              message: 'getCashdrawers: query failure',
              meta_data: getStackTrace(),
              error,
            })
          );
      setCashdrawer(''); // any value for cashdrawer is tied to the previous location
      setLocation(event.target.value);
      setLocationState('success');
      setDirtyData(true);
    }
  };
  const cashdrawerChangeHandler = (event) => {
    if (event.target.value !== cashdrawer) {
      setCashdrawer(event.target.value);
      setCashdrawerState('success');
      setDirtyData(true);
    }
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  const searchResults = () => {
    let results = [];
    if (counts.data.length > 0) {
      results = counts.data.map((count, key) => {
        const result = [];
        const actionButtons = [];
        result.push(count.reason.label);
        const format = getCurrencyFormat(tenant?.base_currency);
        result.push(count.amount.toFormat(format));
        result.push(count.expected.toFormat(format));
        result.push(count.delta.toFormat(format));
        result.push(new Date(count.created_at).toLocaleString());
        actionButtons.push(
          actionButton(ViewIcon, 'warning', handleViewHistory, key)
        );
        result.push(actionButtons);
        return result;
      });
    }
    return results;
  };

  const renderCountHistory = useCallback(() => {
    if (!viewHistory || historyStatus !== 'success') {
      return null;
    }

    const drawerType = cashdrawers.find(
      ({ cashdrawer_id }) => cashdrawer_id === cashdrawer
    )?.type;

    return (
      <CountHistory
        open={viewHistory}
        handleClose={handleCloseHistory}
        history={countHistory}
        type={drawerType}
      />
    );
  }, [viewHistory, historyStatus, cashdrawer, cashdrawers]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <SearchIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Search Criteria</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6}>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='location-select-label'
                    className={classes.inputLabel}
                  >
                    Location
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='location-select-label'
                    id='location-select'
                    value={location}
                    onChange={(event) => locationChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    {locationList()}
                  </Select>
                </FormControl>
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='cashdrawer-select-label'
                    className={classes.inputLabel}
                  >
                    Cash Drawer
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='cashdrawer-select-label'
                    id='cashdrawer-select'
                    value={cashdrawer}
                    onChange={(event) => cashdrawerChangeHandler(event)}
                  >
                    <MenuItem key={-1} value=''>
                      -Empty-
                    </MenuItem>
                    {cashdrawerList()}
                  </Select>
                </FormControl>
                <GridContainer>
                  <GridItem xs={6}>
                    <DatePicker
                      isValidDate={(current) => {
                        return endDate ? current.isBefore(endDate) : true;
                      }}
                      success={startDateState === 'success'}
                      error={startDateState === 'error'}
                      labelText='Start Date'
                      id='startdate'
                      validator={isStartDateValid}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: startDate,
                        onChange: (event) => {
                          setDirtyData(true);
                          setStartDateState('success');
                          setStartDate(event._d);
                        },
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <DatePicker
                      isValidDate={(current) => {
                        return startDate ? current.isAfter(startDate) : true;
                      }}
                      success={endDateState === 'success'}
                      error={endDateState === 'error'}
                      labelText='End Date'
                      id='enddate'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      validator={isEndDateValid}
                      inputProps={{
                        value: endDate,
                        onChange: (event) => {
                          setDirtyData(true);
                          setEndDateState('success');
                          setEndDate(event._d);
                        },
                      }}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem xs={12}>
                <Button
                  color='rose'
                  onClick={onClickSearch}
                  disabled={!dirtyData}
                  className={classes.registerButton}
                >
                  Search
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      {isCashCountsSuccess && counts.data.length > 0 ? (
        <GridItem xs={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <SearchResultsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Cash Counts</h4>
            </CardHeader>
            <CardBody>
              <Table
                tableHead={[
                  'Reason',
                  'Count',
                  'Expected',
                  'Discrepancy',
                  'Date',
                  'Actions',
                ]}
                tableData={searchResults()}
              />
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={resultCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                SelectProps={{
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
      ) : counts ? (
        <GridItem xs={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <SearchResultsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Cash Counts</h4>
            </CardHeader>
            <CardBody>
              <Typography
                variant='body2'
                align='center'
                sx={{ fontWeight: 'bold' }}
              >
                No cash counts, within entered search criteria, to display.
              </Typography>
            </CardBody>
          </Card>
        </GridItem>
      ) : null}
      {renderCountHistory()}
    </GridContainer>
  );
}
